import React from 'react';
import { TableCell } from '@/components/ui/table';
import { Package } from 'lucide-react';
import { ExportTableCell } from './ExportTableCell';
import { DataTable } from './DataTable';

// Protected columns that cannot be dragged
const PROTECTED_COLUMNS = ['marker', 'actions'];

// Cell class mappings for styling
const CELL_CLASSES = {
  booking_number: 'font-mono font-medium text-xs',
  mbl_number: 'font-mono text-xs',
  container_number: 'font-mono text-xs',
  container_size: 'text-xs',
  overweight: 'text-xs text-center',
  ssl: 'text-xs',
  vessel_name: 'text-xs',
  customer_name: 'text-xs',
  destination_port: 'text-xs',
  cutoff_date: 'text-xs',
  erd: 'text-xs',
  load_rdy: 'text-xs',
  load_return: 'text-xs',
  status: 'p-1',
  driver_name: 'text-xs',
  pickup_address: 'text-xs',
  remarks: 'text-xs max-w-[100px] truncate'
};

/**
 * ExportsTable - Refactored to use shared DataTable component
 * Handles rendering of export shipment data with inline editing, sorting, and selection
 */
export default function ExportsTable({
  exports,
  loading,
  columns,
  statusColors,
  statuses,
  masterData = {},
  sortColumn,
  sortDirection,
  onColumnSort,
  // Selection
  isAllSelected,
  isSomeSelected,
  toggleSelectAll,
  isSelected,
  toggleSelectExport,
  // Row styling
  getRowBackgroundColor,
  lastEditedExportId,
  // Actions
  onToggleImportant,
  onOpenDispatch,
  onEditExport,
  onOpenStatusChange,
  onStatusChange,
  onCellColorChange,
  // Inline edit handlers
  onInlineDateSave,
  onChassisSave,
  onPPCTFSave,
  // Inline text edit (e.g. remarks)
  canEdit = true,
  inlineEditCell,
  inlineEditValue,
  setInlineEditValue,
  startInlineEdit,
  handleInlineEditSave,
  cancelInlineEdit,
  // Column drag/resize
  draggedColumn,
  dragOverColumn,
  onDragStart,
  onDragOver,
  onDragEnd,
  onResizeStart,
  // Helper functions
  formatDate,
  formatTime,
  getCellTextColor,
  tableRef
}) {
  // Get visible columns excluding fixed columns (marker, important handled separately)
  const visibleDataColumns = columns.filter(col => col.visible && !['marker', 'important'].includes(col.id));

  // Render cell content using ExportTableCell
  const renderCell = (columnId, exp) => (
    <ExportTableCell
      columnId={columnId}
      exp={exp}
      onToggleImportant={onToggleImportant}
      onOpenDispatch={onOpenDispatch}
      onEditExport={onEditExport}
      onOpenStatusChange={onOpenStatusChange}
      onStatusChange={onStatusChange}
      onCellColorChange={onCellColorChange}
      onInlineDateSave={onInlineDateSave}
      onChassisSave={onChassisSave}
      onPPCTFSave={onPPCTFSave}
      statusColors={statusColors}
      statuses={statuses}
      masterData={masterData}
      formatDate={formatDate}
      formatTime={formatTime}
      getCellTextColor={getCellTextColor}
      canEdit={canEdit}
      inlineEditCell={inlineEditCell}
      inlineEditValue={inlineEditValue}
      setInlineEditValue={setInlineEditValue}
      startInlineEdit={startInlineEdit}
      handleInlineEditSave={handleInlineEditSave}
      cancelInlineEdit={cancelInlineEdit}
    />
  );

  // Render extra leading cells (marker and important star)
  const renderExtraRowCells = (exp) => (
    <>
      {/* Status Marker */}
      <TableCell className="p-0 w-[8px]">
        <ExportTableCell
          columnId="marker"
          exp={exp}
          statusColors={statusColors}
          lastEditedExportId={lastEditedExportId}
        />
      </TableCell>
      {/* Important Star */}
      <TableCell className="p-1 w-[30px]">
        <ExportTableCell
          columnId="important"
          exp={exp}
          onToggleImportant={onToggleImportant}
        />
      </TableCell>
    </>
  );

  // Extra header columns for marker and important
  const extraHeaderColumns = (
    <>
      <th className="w-[8px] p-0"></th>
      <th className="w-[30px] p-1"></th>
    </>
  );

  return (
    <DataTable
      data={exports}
      columns={visibleDataColumns}
      loading={loading}
      emptyMessage='No export bookings yet. Click "New Export" to create one.'
      emptyIcon={Package}
      idField="id"
      // Sorting
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onColumnSort={onColumnSort}
      // Drag & Drop
      draggedColumn={draggedColumn}
      dragOverColumn={dragOverColumn}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      // Resize
      onResizeStart={onResizeStart}
      // Selection
      showSelection={true}
      isAllSelected={isAllSelected}
      isSomeSelected={isSomeSelected}
      onToggleSelectAll={toggleSelectAll}
      isSelected={isSelected}
      onToggleSelect={toggleSelectExport}
      // Styling
      getRowStyle={(exp) => ({
        ...getRowBackgroundColor(exp.status),
        ...(lastEditedExportId === exp.id ? { boxShadow: 'inset 0 0 0 1px rgb(59 130 246)' } : {})
      })}
      getCellStyle={(col) => ({
        width: col.width,
        minWidth: col.width
      })}
      // Rendering
      renderCell={renderCell}
      // Column helpers
      getColumnWidth={(col) => col.width}
      getMinColumnWidth={() => '60px'}
      // Protected columns
      protectedColumns={PROTECTED_COLUMNS}
      // Extra columns
      extraHeaderColumns={extraHeaderColumns}
      renderExtraRowCells={renderExtraRowCells}
      // Ref
      tableRef={tableRef}
      // Test ID
      testIdPrefix="export-row"
      // Mobile hint
      showMobileHint={false}
      // Class names
      className=""
      tableClassName="data-table"
    />
  );
}
